/**
 * openExternal — open a URL in the user's default browser.
 *
 * Why a helper instead of inline `window.electronAPI.openExternalUrl(url)`:
 *   - In Electron, `<a target="_blank" href="…">` either silently no-ops or
 *     opens a new Electron window, which is wrong for marketing/docs links.
 *     The IPC bridge routes through `shell.openExternal` so the OS picks
 *     the right browser.
 *   - In browser/dev mode (no electronAPI), fall back to `window.open(url,
 *     '_blank', 'noopener,noreferrer')` so the same call site works in
 *     both environments without checks at every link.
 *   - The IPC handler refuses non-http(s) schemes for safety; this
 *     wrapper never re-validates so the policy stays in one place
 *     (the main process).
 *
 * Returns a Promise<boolean> — true when the open was dispatched, false
 * on validation/failure. Callers can show a fallback "copy this URL"
 * affordance on false if they want.
 */
export const openExternal = async (url) => {
  if (typeof url !== 'string' || url.length === 0) return false;
  // Electron path
  if (typeof window !== 'undefined' && window.electronAPI?.openExternalUrl) {
    try {
      const r = await window.electronAPI.openExternalUrl(url);
      return !!r?.success;
    } catch {
      return false;
    }
  }
  // Browser fallback (dev mode, web build)
  try {
    if (typeof window !== 'undefined' && typeof window.open === 'function') {
      window.open(url, '_blank', 'noopener,noreferrer');
      return true;
    }
  } catch { /* */ }
  return false;
};

/**
 * Convenience React handler — returns an onClick that prevents default,
 * stops propagation, and dispatches openExternal. Usage:
 *
 *   <a href="https://…" onClick={makeExternalClickHandler('https://…')}>…</a>
 *
 * The href stays in the DOM for accessibility (screen readers + middle-click)
 * even though the click handler intercepts the navigation.
 */
export const makeExternalClickHandler = (url) => (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
  openExternal(url);
};
